import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Search, Plus, Pencil, Trash2, X, Shield, ShieldCheck,
  Lock, AlertTriangle, ChevronLeft, ChevronRight, Settings2, UserPlus, Eye, EyeOff
} from 'lucide-react';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { usePermissions } from '../../admin/usePermissions';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import PermissionMatrix from './PermissionMatrix';

const STATUS_STYLE = { active: 'bg-emerald-50 text-emerald-700', inactive: 'bg-gray-100 text-gray-600' };

function errMsg(e) {
  return e?.response?.data?.message || e?.response?.data?.detail || adminErrorMsg(e);
}

export default function AdminUserPrivilegesPage() {
  const { has } = usePermissions();
  const [tab, setTab] = useState('roles');

  /* ── Shared data ── */
  const [allPerms, setAllPerms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [permsLoading, setPermsLoading] = useState(true);

  /* ── Admin Users tab ── */
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersQ, setUsersQ] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('');
  const [usersOffset, setUsersOffset] = useState(0);
  const usersLimit = 25;

  /* ── Create Role ── */
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createPerms, setCreatePerms] = useState(new Set());
  const [createSaving, setCreateSaving] = useState(false);

  /* ── Edit Role ── */
  const [editRole, setEditRole] = useState(null);
  const [editPerms, setEditPerms] = useState(new Set());
  const [editDirty, setEditDirty] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  /* ── Rename ── */
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  /* ── Delete ── */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Overrides ── */
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [overrideOriginal, setOverrideOriginal] = useState({});
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);

  /* ── Add User ── */
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [auName, setAuName] = useState('');
  const [auEmail, setAuEmail] = useState('');
  const [auPassword, setAuPassword] = useState('');
  const [auShowPw, setAuShowPw] = useState(false);
  const [auStatus, setAuStatus] = useState('active');
  const [auRoleId, setAuRoleId] = useState('');
  const [auOverridesExpanded, setAuOverridesExpanded] = useState(false);
  const [auOverrides, setAuOverrides] = useState({});
  const [auSaving, setAuSaving] = useState(false);

  /* ── Fetchers ── */
  const loadPerms = useCallback(async () => {
    try { const r = await adminApi.get('/permissions'); setAllPerms(r.data?.items || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setPermsLoading(false); }
  }, []);

  const loadRoles = useCallback(async () => {
    try { setRolesLoading(true); const r = await adminApi.get('/roles', { params: { limit: 100 } }); setRoles(r.data?.items || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setRolesLoading(false); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const params = { limit: usersLimit, offset: usersOffset };
      if (usersQ) params.q = usersQ;
      if (usersRoleFilter) params.role_id = usersRoleFilter;
      const r = await adminApi.get('/admin-users', { params });
      setAdminUsers(r.data?.items || []); setUsersTotal(r.data?.total ?? 0);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUsersLoading(false); }
  }, [usersQ, usersRoleFilter, usersOffset]);

  useEffect(() => { loadPerms(); loadRoles(); }, [loadPerms, loadRoles]);
  useEffect(() => { setUsersLoading(true); loadUsers(); }, [loadUsers]);

  /* ── Handlers ── */
  const resetCreate = () => { setCreateOpen(false); setCreateName(''); setCreateDesc(''); setCreatePerms(new Set()); };

  const handleCreateRole = async () => {
    if (!createName.trim()) { toast.error('Role name is required'); return; }
    setCreateSaving(true);
    try {
      await adminApi.post('/roles', { name: createName.trim(), description: createDesc.trim(), permission_keys: [...createPerms] });
      toast.success('Role created'); resetCreate(); await loadRoles();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCreateSaving(false); }
  };

  const openEditRole = (role) => { setEditRole(role); setEditPerms(new Set(role.permission_keys || [])); setEditDirty(false); };

  const handleSavePermissions = async () => {
    if (!editRole) return;
    setEditSaving(true);
    try {
      await adminApi.put(`/roles/${editRole.id}/permissions`, { permission_keys: [...editPerms] });
      toast.success('Permissions updated'); setEditRole(null); setEditDirty(false); await loadRoles();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setEditSaving(false); }
  };

  const closeEditDrawer = () => { if (editDirty && !window.confirm('You have unsaved changes. Discard?')) return; setEditRole(null); setEditDirty(false); };

  const handleRename = async () => {
    if (!renameName.trim()) return;
    setRenameSaving(true);
    try { await adminApi.patch(`/roles/${renameTarget.id}`, { name: renameName.trim() }); toast.success('Role renamed'); setRenameTarget(null); await loadRoles(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setRenameSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await adminApi.delete(`/roles/${deleteTarget.id}`); toast.success('Role deleted'); setDeleteTarget(null); await loadRoles(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setDeleting(false); }
  };

  const handleRoleChange = async (userId, newRoleId) => {
    const prev = adminUsers.find(u => u.id === userId);
    setAdminUsers(us => us.map(u => u.id === userId ? { ...u, role_id: newRoleId || null, role_name: roles.find(r => r.id === newRoleId)?.name || null } : u));
    try {
      await adminApi.patch(`/admin-users/${userId}/role`, { role_id: newRoleId || null });
      toast.success('Role updated'); await loadUsers(); await loadRoles();
    } catch (e) { toast.error(errMsg(e)); if (prev) setAdminUsers(us => us.map(u => u.id === userId ? prev : u)); }
  };

  const openOverrides = async (user) => {
    setOverrideTarget(user); setOverrideLoading(true);
    try {
      const r = await adminApi.get(`/admin-users/${user.id}/overrides`);
      const map = {}; (r.data?.items || []).forEach(o => { map[o.permission_key] = o.granted; });
      setOverrides(map); setOverrideOriginal({ ...map });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setOverrideLoading(false); }
  };

  const handleSaveOverrides = async () => {
    setOverrideSaving(true);
    try {
      const payload = Object.entries(overrides).map(([permission_key, granted]) => ({ permission_key, granted }));
      await adminApi.put(`/admin-users/${overrideTarget.id}/overrides`, { overrides: payload });
      toast.success('Overrides saved'); setOverrideTarget(null); await loadUsers();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setOverrideSaving(false); }
  };

  const closeOverrideDrawer = () => {
    if (JSON.stringify(overrides) !== JSON.stringify(overrideOriginal) && !window.confirm('You have unsaved changes. Discard?')) return;
    setOverrideTarget(null);
  };

  /* ── Add User handler ── */
  const resetAddUser = () => { setAddUserOpen(false); setAuName(''); setAuEmail(''); setAuPassword(''); setAuShowPw(false); setAuStatus('active'); setAuRoleId(''); setAuOverridesExpanded(false); setAuOverrides({}); };

  const handleAddUser = async () => {
    if (!auName.trim() || auName.trim().length < 2) { toast.error('Full name must be at least 2 characters'); return; }
    if (!auEmail.trim()) { toast.error('Email is required'); return; }
    if (!auPassword || auPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!auRoleId) { toast.error('Please select a role'); return; }
    setAuSaving(true);
    try {
      const body = { full_name: auName.trim(), email: auEmail.trim().toLowerCase(), password: auPassword, role_id: auRoleId, status: auStatus };
      const overridesList = Object.entries(auOverrides).map(([permission_key, granted]) => ({ permission_key, granted }));
      if (overridesList.length > 0) body.overrides = overridesList;
      await adminApi.post('/admin-users', body);
      toast.success('Admin user created');
      resetAddUser();
      await loadUsers(); await loadRoles();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAuSaving(false); }
  };

  /* ── Add User: computed overrides for the selected role ── */
  const auSelectedRole = useMemo(() => roles.find(r => r.id === auRoleId), [roles, auRoleId]);
  const auRolePerms = useMemo(() => new Set(auSelectedRole?.permission_keys || []), [auSelectedRole]);
  const auResolvedSet = useMemo(() => {
    const s = new Set(auRolePerms);
    Object.entries(auOverrides).forEach(([k, granted]) => { if (granted) s.add(k); else s.delete(k); });
    return s;
  }, [auRolePerms, auOverrides]);
  const auOverrideIndicators = useMemo(() => {
    const map = {};
    Object.entries(auOverrides).forEach(([k, granted]) => { map[k] = granted ? 'grant' : 'revoke'; });
    return map;
  }, [auOverrides]);
  const isSuperAdminRole = (roleId) => roles.find(r => r.id === roleId)?.name === 'Super Admin';
  const currentAdminIsSA = has('*');

  /* ── Override computed state ── */
  const targetRolePerms = useMemo(() => {
    if (!overrideTarget?.role_id) return new Set();
    const role = roles.find(r => r.id === overrideTarget.role_id);
    return new Set(role?.permission_keys || []);
  }, [overrideTarget, roles]);

  const resolvedOverrideSet = useMemo(() => {
    const s = new Set(targetRolePerms);
    Object.entries(overrides).forEach(([k, granted]) => { if (granted) s.add(k); else s.delete(k); });
    return s;
  }, [targetRolePerms, overrides]);

  const overrideIndicators = useMemo(() => {
    const map = {};
    Object.entries(overrides).forEach(([k, granted]) => { map[k] = granted ? 'grant' : 'revoke'; });
    return map;
  }, [overrides]);

  const usersPages = Math.ceil(usersTotal / usersLimit);
  const usersPage = Math.floor(usersOffset / usersLimit) + 1;

  /* ── Permission gate ── */
  if (!has('user_privileges.view')) {
    return (
      <div data-testid="no-permission-placeholder" className="flex flex-col items-center justify-center py-24 text-center">
        <Lock className="w-12 h-12 text-gray-300 mb-4" />
        <h2 className="text-lg font-bold text-gray-900 mb-1">No Permission</h2>
        <p className="text-sm text-gray-500">You don&apos;t have permission to view this page.</p>
      </div>
    );
  }

  const isSA = (r) => r.name === 'Super Admin';

  return (
    <div data-testid="admin-user-privileges-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-black text-gray-900">User Privileges</h1>
        <div className="flex gap-2">
          {tab === 'users' && has('user_privileges.manage_roles') && (
            <button data-testid="add-user-btn" onClick={() => setAddUserOpen(true)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold hover:bg-pink-700 transition-colors">
              <UserPlus className="w-4 h-4" /> Add User
            </button>
          )}
          {tab === 'roles' && has('user_privileges.manage_roles') && (
            <button data-testid="create-role-btn" onClick={() => setCreateOpen(true)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold hover:bg-pink-700 transition-colors">
              <Plus className="w-4 h-4" /> Create Role
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        {[{ key: 'roles', label: 'Roles' }, { key: 'users', label: 'Admin Users' }].map(t => (
          <button key={t.key} data-testid={`privileges-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-5 py-2 rounded-md text-sm font-bold transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════ ROLES TAB ═══════ */}
      {tab === 'roles' && (rolesLoading ? <Spinner /> : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="roles-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <TH>Name</TH><TH className="hidden md:table-cell">Description</TH><TH>Type</TH><TH>Status</TH><TH className="text-center">Perms</TH><TH className="text-center">Admins</TH><TH className="text-right">Actions</TH>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {roles.map(r => (
                  <tr key={r.id} data-testid={`role-row-${r.id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      <div className="flex items-center gap-2">{isSA(r) ? <ShieldCheck className="w-4 h-4 text-brand-magenta" /> : <Shield className="w-4 h-4 text-gray-400" />}{r.name}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell max-w-[240px] truncate">{r.description}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${r.is_system_role ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>{r.is_system_role ? 'SYSTEM' : 'CUSTOM'}</span></td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>{(r.status || 'active').toUpperCase()}</span></td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-gray-700">{(r.permission_keys || []).length}</td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-gray-700">{r.admin_user_count ?? 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`role-edit-btn-${r.id}`} onClick={() => openEditRole(r)} className="p-1.5 rounded hover:bg-pink-50 transition-colors" title="View / Edit permissions"><Settings2 className="w-4 h-4 text-brand-magenta" /></button>
                        {!r.is_system_role && has('user_privileges.manage_roles') && (
                          <button data-testid={`role-rename-btn-${r.id}`} onClick={() => { setRenameTarget(r); setRenameName(r.name); }} className="p-1.5 rounded hover:bg-blue-50 transition-colors" title="Rename"><Pencil className="w-4 h-4 text-blue-600" /></button>
                        )}
                        {!r.is_system_role && has('user_privileges.manage_roles') && r.admin_user_count === 0 && (
                          <button data-testid={`role-delete-btn-${r.id}`} onClick={() => setDeleteTarget(r)} className="p-1.5 rounded hover:bg-red-50 transition-colors" title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {roles.length === 0 && <div className="text-center py-12"><Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No custom roles yet — start with a system role or create one.</p></div>}
        </div>
      ))}

      {/* ═══════ ADMIN USERS TAB ═══════ */}
      {tab === 'users' && (<>
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400" />
            <input data-testid="admin-users-search-input" value={usersQ} onChange={e => { setUsersQ(e.target.value); setUsersOffset(0); }} placeholder="Search email or name..." className="py-2 text-sm text-gray-900 bg-transparent focus:outline-none flex-1" />
          </div>
          <select data-testid="admin-users-role-filter" value={usersRoleFilter} onChange={e => { setUsersRoleFilter(e.target.value); setUsersOffset(0); }} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700">
            <option value="">All Roles</option>
            {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        {usersLoading ? <Spinner /> : (<>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <TH>Email</TH><TH>Full Name</TH><TH>Role</TH><TH>Status</TH><TH className="hidden md:table-cell">Last Login</TH><TH className="text-right">Actions</TH>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {adminUsers.map(u => {
                    const isSeeded = u.role === 'super_admin' && !u.role_id;
                    return (
                      <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-semibold text-gray-900">{u.email}</td>
                        <td className="px-4 py-3 text-gray-600">{u.full_name}</td>
                        <td className="px-4 py-3">
                          {isSeeded ? (
                            <span className="text-xs font-bold text-brand-magenta bg-pink-50 px-2 py-1 rounded-lg">Super Admin (seeded)</span>
                          ) : has('user_privileges.assign_roles') ? (
                            <select data-testid={`admin-user-role-select-${u.id}`} value={u.role_id || ''} onChange={e => handleRoleChange(u.id, e.target.value)}
                              className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-700 max-w-[160px]">
                              <option value="">Unassigned</option>
                              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                          ) : (
                            <span className="text-xs font-bold text-brand-magenta bg-pink-50 px-2 py-0.5 rounded-full">{u.role_name || 'Unassigned'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{u.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">{u.last_login_at ? dayjs(u.last_login_at).format('MMM D, YYYY h:mm A') : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          {!isSeeded && has('user_privileges.manage_permissions') && (
                            <button data-testid={`admin-user-override-btn-${u.id}`} onClick={() => openOverrides(u)} className="text-xs font-bold text-brand-magenta hover:text-pink-700">Manage Overrides</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {adminUsers.length === 0 && <p className="text-center text-sm text-gray-400 py-10">No admin users found</p>}
          </div>

          {usersPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">{usersTotal} total · Page {usersPage}/{usersPages}</p>
              <div className="flex gap-1">
                <button disabled={usersOffset === 0} onClick={() => setUsersOffset(Math.max(0, usersOffset - usersLimit))} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
                <button disabled={usersPage >= usersPages} onClick={() => setUsersOffset(usersOffset + usersLimit)} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </>)}
      </>)}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-100 text-center">
        <Link to="/admin/logs" data-testid="activity-log-link" className="text-sm text-brand-magenta font-semibold hover:underline">See recent changes in the Activity Log →</Link>
      </div>

      {/* ═══════ CREATE ROLE DRAWER ═══════ */}
      {createOpen && (
        <Overlay onClose={resetCreate}>
          <Drawer data-testid="create-role-dialog" title="Create Role" onClose={resetCreate}>
            <div className="p-6 space-y-5">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Role Name *</label>
                <input data-testid="create-role-name-input" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. Content Editor" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm" autoFocus />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Description</label>
                <input data-testid="create-role-desc-input" value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder="Brief description of this role" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-2 block">Permissions ({createPerms.size})</label>
                {permsLoading ? <Loader2 className="w-5 h-5 animate-spin text-brand-magenta" /> : <PermissionMatrix allPermissions={allPerms} selected={createPerms} onChange={setCreatePerms} />}
              </div>
            </div>
            <DrawerFooter>
              <button onClick={resetCreate} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="create-role-save-btn" onClick={handleCreateRole} disabled={createSaving || !createName.trim()} className="flex-1 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                {createSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create Role'}
              </button>
            </DrawerFooter>
          </Drawer>
        </Overlay>
      )}

      {/* ═══════ EDIT ROLE DRAWER ═══════ */}
      {editRole && (
        <Overlay onClose={closeEditDrawer}>
          <Drawer data-testid="role-edit-drawer" title={editRole.name} subtitle={editRole.description} onClose={closeEditDrawer}>
            <div className="p-6">
              {isSA(editRole) && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="super-admin-warning">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 font-medium">Super Admin always has all permissions. Checkboxes are read-only.</p>
                </div>
              )}
              <label className="text-xs font-bold text-gray-500 mb-2 block">Permissions ({editPerms.size})</label>
              {permsLoading ? <Loader2 className="w-5 h-5 animate-spin text-brand-magenta" /> : (
                <PermissionMatrix allPermissions={allPerms} selected={editPerms} onChange={s => { setEditPerms(s); setEditDirty(true); }}
                  disabled={isSA(editRole) || !has('user_privileges.manage_permissions')} disabledTooltip={isSA(editRole) ? 'Super Admin always has all permissions' : 'You lack manage_permissions'} />
              )}
            </div>
            {!isSA(editRole) && has('user_privileges.manage_permissions') && (
              <DrawerFooter>
                <button data-testid="role-edit-save-btn" onClick={handleSavePermissions} disabled={editSaving || !editDirty}
                  className="w-full py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                  {editSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save Permissions'}
                </button>
              </DrawerFooter>
            )}
          </Drawer>
        </Overlay>
      )}

      {/* ═══════ RENAME DIALOG ═══════ */}
      {renameTarget && (
        <Overlay onClose={() => setRenameTarget(null)}>
          <div className="relative bg-white rounded-xl w-full max-w-sm p-6 mx-4 shadow-xl" data-testid="rename-dialog">
            <h3 className="text-base font-bold text-gray-900 mb-4">Rename Role</h3>
            <label className="text-xs font-bold text-gray-500 mb-1 block">New Name</label>
            <input data-testid="rename-input" value={renameName} onChange={e => setRenameName(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm mb-4" autoFocus />
            <div className="flex gap-2">
              <button onClick={() => setRenameTarget(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="rename-save-btn" onClick={handleRename} disabled={renameSaving || !renameName.trim() || renameName.trim() === renameTarget.name}
                className="flex-1 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                {renameSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Rename'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ═══════ DELETE DIALOG ═══════ */}
      {deleteTarget && (
        <Overlay onClose={() => setDeleteTarget(null)}>
          <div className="relative bg-white rounded-xl w-full max-w-sm p-6 mx-4 shadow-xl" data-testid="delete-confirm-dialog">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-500" /></div>
              <div><h3 className="text-base font-bold text-gray-900">Delete Role</h3><p className="text-xs text-gray-500">&ldquo;{deleteTarget.name}&rdquo; will be permanently removed.</p></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="delete-confirm-btn" onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-50 hover:bg-red-700 transition-colors">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Delete'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ═══════ OVERRIDE DRAWER ═══════ */}
      {overrideTarget && (
        <Overlay onClose={closeOverrideDrawer}>
          <Drawer data-testid="override-drawer" title="Manage Overrides" subtitle={`${overrideTarget.full_name} — ${overrideTarget.email}`} onClose={closeOverrideDrawer}>
            <div className="p-6">
              {overrideLoading ? <Spinner /> : (<>
                <div className="flex items-center gap-3 mb-4 bg-gray-50 rounded-lg p-3">
                  <Shield className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <p className="text-xs"><span className="font-bold text-gray-700">Base role: </span><span className="text-gray-500">{overrideTarget.role_name || 'Unassigned'} ({targetRolePerms.size} permissions)</span></p>
                </div>
                <div className="flex gap-4 mb-4 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Granted</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Revoked</span>
                </div>
                <PermissionMatrix allPermissions={allPerms} selected={resolvedOverrideSet} overrideIndicators={overrideIndicators}
                  onChange={(newSet) => {
                    const next = {};
                    allPerms.forEach(p => {
                      const inRole = targetRolePerms.has(p.key);
                      const inNew = newSet.has(p.key);
                      if (inRole && !inNew) next[p.key] = false;
                      else if (!inRole && inNew) next[p.key] = true;
                    });
                    setOverrides(next);
                  }} />
                <div className="mt-4 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-700">Resolved: {resolvedOverrideSet.size} permissions</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{Object.values(overrides).filter(v => v).length} granted, {Object.values(overrides).filter(v => !v).length} revoked overrides</p>
                </div>
              </>)}
            </div>
            {!overrideLoading && (
              <DrawerFooter>
                <button data-testid="override-save-btn" onClick={handleSaveOverrides} disabled={overrideSaving}
                  className="w-full py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                  {overrideSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save Overrides'}
                </button>
              </DrawerFooter>
            )}
          </Drawer>
        </Overlay>
      )}

      {/* ═══════ ADD USER DRAWER ═══════ */}
      {addUserOpen && (
        <Overlay onClose={resetAddUser}>
          <Drawer data-testid="add-user-drawer" title="Add Admin User" onClose={resetAddUser}>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Full Name *</label>
                <input data-testid="add-user-name-input" value={auName} onChange={e => setAuName(e.target.value)} placeholder="Jane Doe" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm" autoFocus />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Email Address *</label>
                <input data-testid="add-user-email-input" type="email" value={auEmail} onChange={e => setAuEmail(e.target.value)} placeholder="jane@radius.app" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Password *</label>
                <div className="relative">
                  <input data-testid="add-user-password-input" type={auShowPw ? 'text' : 'password'} value={auPassword} onChange={e => setAuPassword(e.target.value)} placeholder="Min 8 characters" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 pr-10 text-sm" />
                  <button type="button" data-testid="add-user-toggle-pw" onClick={() => setAuShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {auShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Status</label>
                <div className="flex gap-3">
                  {['active', 'inactive'].map(s => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer" data-testid={`add-user-status-${s}`}>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${auStatus === s ? 'border-brand-magenta' : 'border-gray-300'}`}>
                        {auStatus === s && <div className="w-2 h-2 rounded-full bg-brand-magenta" />}
                      </div>
                      <span className="text-sm text-gray-700 capitalize">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1.5 block">Role *</label>
                <select data-testid="add-user-role-select" value={auRoleId} onChange={e => { setAuRoleId(e.target.value); setAuOverrides({}); }} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm">
                  <option value="">Select a role...</option>
                  {roles.filter(r => currentAdminIsSA || r.name !== 'Super Admin').map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              {auRoleId && !isSuperAdminRole(auRoleId) && (
                <div>
                  <button type="button" data-testid="add-user-overrides-toggle" onClick={() => setAuOverridesExpanded(v => !v)} className="flex items-center gap-2 text-xs font-bold text-brand-magenta hover:text-pink-700">
                    {auOverridesExpanded ? '▾' : '▸'} Custom Permissions / Overrides
                  </button>
                  {auOverridesExpanded && !permsLoading && (
                    <div className="mt-3">
                      <PermissionMatrix allPermissions={allPerms} selected={auResolvedSet} overrideIndicators={auOverrideIndicators}
                        onChange={(newSet) => {
                          const next = {};
                          allPerms.forEach(p => {
                            const inRole = auRolePerms.has(p.key);
                            const inNew = newSet.has(p.key);
                            if (inRole && !inNew) next[p.key] = false;
                            else if (!inRole && inNew) next[p.key] = true;
                          });
                          setAuOverrides(next);
                        }} />
                      <div className="mt-2 bg-gray-50 rounded-lg p-2.5">
                        <p className="text-[11px] text-gray-500">Effective: {auResolvedSet.size} permissions (from role) + {Object.keys(auOverrides).length} overrides</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DrawerFooter>
              <button onClick={resetAddUser} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="add-user-submit-btn" onClick={handleAddUser} disabled={auSaving || !auName.trim() || !auEmail.trim() || !auPassword || !auRoleId}
                className="flex-1 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                {auSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create User'}
              </button>
            </DrawerFooter>
          </Drawer>
        </Overlay>
      )}
    </div>
  );
}

/* ── Shared UI helpers ── */
function Spinner() { return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>; }
function TH({ children, className = '' }) { return <th className={`text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider ${className}`}>{children}</th>; }
function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {children}
    </div>
  );
}
function Drawer({ children, title, subtitle, onClose, ...rest }) {
  return (
    <div className="relative bg-white h-full w-full max-w-lg overflow-y-auto shadow-xl ml-auto" {...rest}>
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
        <div><h2 className="text-base font-black text-gray-900">{title}</h2>{subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}</div>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  );
}
function DrawerFooter({ children }) {
  return <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-2">{children}</div>;
}
